import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ArrowRight,
  FileCheck,
  Loader2,
  Repeat,
  Settings2,
  Sparkles,
} from "lucide-react";

import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import { OnboardingBrandMark } from "@/components/onboarding/OnboardingBrandMark";
import { OnboardingStepTag } from "@/components/onboarding/OnboardingStepTag";
import { OnboardingBrandColumn } from "@/components/onboarding/OnboardingBrandColumn";
import { getDgiConfigTrail } from "@/components/onboarding/OnboardingTrail";
import { OnboardingChoiceCard } from "@/components/onboarding/OnboardingChoiceCard";
import { OnboardingProviderRequestNoticeStep } from "@/pages/onboarding/OnboardingProviderRequestNoticeStep";

export type SituationChoice =
  | "scratch"
  | "issuer_need_regularization"
  | "already_issuer";

/** Auto-detected split for the "ya facturé antes" answer (from DGI's padrón). */
export type DetectedEmisorStatus = "already_issuer" | "issuer_need_regularization";

export interface OnboardingSituationStepProps {
  /** Tenant legal/trade name (kept for the provider-request notice template). */
  companyDisplayName?: string;
  /** Tenant RUT — surfaced inside the provider-request notice template. */
  tenantRut?: string | null;
  /** Whether the tenant is migrating legacy CFEs — used by the already_issuer
   *  provider-request notice + trail. Defaults to false. */
  isMigratingCfes?: boolean;
  /** Resolve the already_issuer vs regularization split for "ya facturé antes".
   *  Fails soft to already_issuer (never rejects). */
  onDetectEmisorStatus: () => Promise<DetectedEmisorStatus>;
  /** Persist the resolved situation choice (+ whether they migrate) and advance. */
  onSubmit: (
    choice: SituationChoice,
    opts?: { migratingFromAnotherPlatform?: boolean },
  ) => void | Promise<void>;
  onBack: () => void;
  isSaving?: boolean;
}

/** Has the tenant ever invoiced electronically. */
type InvoicedBefore = "nunca" | "facture";
/** For an existing emisor: bringing data from a prior provider, or configuring fresh. */
type AlreadyIssuerMode = "migrate" | "self";

interface ChoiceCardCopy {
  icon: typeof Sparkles;
  nameLead: string;
  nameAccent: string;
  desc: string;
  outcome: string;
}

export function OnboardingSituationStep({
  companyDisplayName,
  tenantRut = null,
  isMigratingCfes = false,
  onDetectEmisorStatus,
  onSubmit,
  onBack,
  isSaving = false,
}: OnboardingSituationStepProps): JSX.Element {
  const { t } = useTranslation();
  // Stages of this one wizard step:
  //   select              — ¿facturaste antes? (nunca → scratch; facturé → detect)
  //   alreadyIssuerChoice — active emisor: ¿migrás de otro proveedor o configurás
  //                         desde cero? (decides migratingFromAnotherPlatform)
  //   providerNotice      — the "pedile a tu proveedor" cert/CFE request (migrate only)
  const [stage, setStage] = useState<
    "select" | "alreadyIssuerChoice" | "providerNotice"
  >("select");
  const [invoicedBefore, setInvoicedBefore] = useState<InvoicedBefore | null>(
    null,
  );
  const [alreadyMode, setAlreadyMode] = useState<AlreadyIssuerMode | null>(null);
  const [detecting, setDetecting] = useState(false);

  const trailSteps = getDgiConfigTrail({
    situationChoice: null,
    isMigratingCfes,
  });

  const brandColumn = (
    <OnboardingBrandColumn
      eyebrow={t(
        "onboarding.situationStep.brandEyebrow",
        "Te lo preguntamos una vez",
      )}
      quote={
        <>
          {t("onboarding.situationStep.brandQuoteLead", "No volvemos a preguntar.")}{" "}
          <span className="accent">
            {t("onboarding.situationStep.brandQuoteAccent", "Adaptamos Fiscalo")}
          </span>{" "}
          {t(
            "onboarding.situationStep.brandQuoteTail",
            "a lo que ya tenés andando.",
          )}
        </>
      }
      currentStep={1}
      trailSteps={trailSteps}
      valueBlock={{
        head: t("onboarding.situationStep.valueHead", "Qué pasa después"),
        items: [
          t("onboarding.situationStep.defaultNext1", "Adaptamos los pasos a tu situación"),
          t(
            "onboarding.situationStep.defaultNext2",
            "Te decimos exactamente qué hace Fiscalo y qué hacés vos",
          ),
          t("onboarding.situationStep.defaultNext3", "Si algo falla, nuestro equipo lo soluciona"),
        ],
      }}
    />
  );

  if (stage === "providerNotice") {
    return (
      <OnboardingProviderRequestNoticeStep
        companyDisplayName={companyDisplayName ?? ""}
        tenantRut={tenantRut}
        isMigratingCfes={isMigratingCfes}
        isSaving={isSaving}
        onContinue={() =>
          void onSubmit("already_issuer", { migratingFromAnotherPlatform: true })
        }
        onBack={() => setStage("alreadyIssuerChoice")}
      />
    );
  }

  const busy = isSaving || detecting;

  /** Shared shell for the two-card questions (select + alreadyIssuerChoice). */
  const questionShell = (params: {
    h1: string;
    lead: string;
    options: [string, ChoiceCardCopy][];
    selected: string | null;
    onSelect: (value: string) => void;
    onContinue: () => void;
    onBack: () => void;
    ctaDisabled: boolean;
  }): JSX.Element => (
    <OnboardingShell
      form={
        <>
          <OnboardingBrandMark />
          <OnboardingStepTag
            step={1}
            total={3}
            label={t("onboarding.situationStep.tag", "Situación")}
            flow={t("onboarding.situationStep.flow", "Configuración DGI")}
          />
          <h1 className="fx-onb-h1">{params.h1}</h1>
          <p className="fx-onb-lead">{params.lead}</p>

          <div className="fx-onb-choice-grid stack">
            {params.options.map(([value, copy]) => {
              const Icon = copy.icon;
              return (
                <OnboardingChoiceCard
                  key={value}
                  icon={<Icon aria-hidden />}
                  name={
                    <>
                      {copy.nameLead}{" "}
                      <span className="ital">{copy.nameAccent}</span>
                    </>
                  }
                  desc={copy.desc}
                  meta={<span className="meta-outcome">{copy.outcome}</span>}
                  selected={params.selected === value}
                  onClick={() => params.onSelect(value)}
                />
              );
            })}
          </div>

          <div className="fx-onb-actions">
            <div className="side">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-secondary"
                onClick={params.onBack}
                disabled={busy}
              >
                <ArrowLeft aria-hidden />
                {t("onboarding.back", "Volver")}
              </button>
            </div>
            <div className="side end">
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-accent"
                disabled={params.ctaDisabled}
                onClick={params.onContinue}
              >
                {busy ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <>
                    {t("onboarding.next", "Continuar")}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      }
      brand={brandColumn}
    />
  );

  if (stage === "alreadyIssuerChoice") {
    const options: [string, ChoiceCardCopy][] = [
      [
        "migrate",
        {
          icon: Repeat,
          nameLead: t("onboarding.situationStep.migrateLead", "Vengo de"),
          nameAccent: t("onboarding.situationStep.migrateAccent", "otro proveedor."),
          desc: t(
            "onboarding.situationStep.migrateDesc",
            "Facturo con otro sistema y quiero traer mi certificado y mis CFE.",
          ),
          outcome: t(
            "onboarding.situationStep.migrateOutcome",
            "Te pasamos la plantilla para pedírselos a tu proveedor actual",
          ),
        },
      ],
      [
        "self",
        {
          icon: Settings2,
          nameLead: t("onboarding.situationStep.selfLead", "Configuro"),
          nameAccent: t("onboarding.situationStep.selfAccent", "desde cero."),
          desc: t(
            "onboarding.situationStep.selfDesc",
            "Ya tengo mi certificado (o lo consigo yo) y no necesito importar comprobantes.",
          ),
          outcome: t(
            "onboarding.situationStep.selfOutcome",
            "Vamos directo a configurar Fiscalo",
          ),
        },
      ],
    ];
    return questionShell({
      h1: t(
        "onboarding.situationStep.alreadyH1",
        "¿Venís de otro proveedor de facturación?",
      ),
      lead: t(
        "onboarding.situationStep.alreadyLead",
        "Si migrás, te ayudamos a traer tu certificado y tus CFE. Si no, configurás Fiscalo desde cero.",
      ),
      options,
      selected: alreadyMode,
      onSelect: (v) => setAlreadyMode(v as AlreadyIssuerMode),
      onBack: () => setStage("select"),
      ctaDisabled: alreadyMode === null || busy,
      onContinue: () => {
        if (alreadyMode === null || busy) return;
        if (alreadyMode === "migrate") {
          setStage("providerNotice");
        } else {
          void onSubmit("already_issuer", {
            migratingFromAnotherPlatform: false,
          });
        }
      },
    });
  }

  // stage === "select": the single invoicing question.
  const selectOptions: [string, ChoiceCardCopy][] = [
    [
      "nunca",
      {
        icon: Sparkles,
        nameLead: t("onboarding.situationStep.q2NuncaLead", "Nunca facturé"),
        nameAccent: t("onboarding.situationStep.q2NuncaAccent", "electrónicamente."),
        desc: t(
          "onboarding.situationStep.q2NuncaDesc",
          "No estoy registrado como emisor electrónico ni tengo certificado digital.",
        ),
        outcome: t(
          "onboarding.situationStep.q2NuncaOutcome",
          "Te damos de alta como emisor y conseguimos tu certificado",
        ),
      },
    ],
    [
      "facture",
      {
        icon: FileCheck,
        nameLead: t("onboarding.situationStep.q2FactureLead", "Ya facturé"),
        nameAccent: t("onboarding.situationStep.q2FactureAccent", "antes."),
        desc: t(
          "onboarding.situationStep.q2FactureDesc",
          "Facturo o facturé electrónicamente con certificado digital.",
        ),
        outcome: t(
          "onboarding.situationStep.q2FactureOutcome",
          "Detectamos tu situación en DGI y configuramos Fiscalo",
        ),
      },
    ],
  ];

  return questionShell({
    h1: t(
      "onboarding.situationStep.q2H1",
      "¿Facturaste electrónicamente antes?",
    ),
    lead: t(
      "onboarding.situationStep.q2Lead",
      "Consultamos en DGI si tu RUT ya es emisor electrónico activo y adaptamos los pasos.",
    ),
    options: selectOptions,
    selected: invoicedBefore,
    onSelect: (v) => setInvoicedBefore(v as InvoicedBefore),
    onBack,
    ctaDisabled: invoicedBefore === null || busy,
    onContinue: () => {
      if (invoicedBefore === null || busy) return;
      if (invoicedBefore === "nunca") {
        void onSubmit("scratch");
        return;
      }
      // "facture" → auto-detect against DGI's padrón.
      setDetecting(true);
      void onDetectEmisorStatus()
        .then((status) => {
          if (status === "already_issuer") {
            setStage("alreadyIssuerChoice");
          } else {
            void onSubmit("issuer_need_regularization");
          }
        })
        .finally(() => setDetecting(false));
    },
  });
}
